import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { intersection } from 'lodash';
import Column from '@amzn/meridian/column';
import Select, { SelectOption, SelectOptionGroup } from '@amzn/meridian/select';
import { useTranslation } from 'src/arb-wrapper';
import { lacSpanishLocale, LAC_SPANISH_COUNTRIES } from 'src/constants';
import type { FormSelectProps } from 'src/components/common/forms/FormSelect';

interface FormLocalesProps extends Omit<FormSelectProps, 'options'> {
  selectedCountry: string;
  supportedLocales: string[];
}

const getDataTestId = (obj: Record<string, unknown>, suffix: string) => {
  const dataTestIdPrefix = obj['data-testid'] as string | undefined;
  const dataTestId = dataTestIdPrefix
    ? `${dataTestIdPrefix}-option-${suffix}`
    : undefined;

  return dataTestId;
};

export const FormLocales: React.FC<FormLocalesProps> = (props) => {
  const {
    control,
    name,
    required,
    selectedCountry,
    supportedLocales,
    ...meridianProps
  } = props;
  const { t } = useTranslation();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? t('form_validation_required_field') : undefined,
    },
  });
  const [shadowFieldValue, setShadowFieldValue] = useState<string | undefined>(undefined);
  const [localesForSelectedCountry, setLocalesForSelectedCountry] = useState<string[]>([]);
  const [localesNotForSelectedCountry, setLocalesNotForSelectedCountry] = useState<string[]>([]);

  const label = useMemo(() => t('form_locale'), [t]);

  useEffect(() => {
    const countryRegex = new RegExp(`_${selectedCountry}$`);
    const countryLocales: string[] = [];
    const notCountryLocales: string[] = [];

    for (const locale of supportedLocales) {
      const matchesCountry = countryRegex.test(locale);
      if ((LAC_SPANISH_COUNTRIES as string[]).includes(selectedCountry) && locale === lacSpanishLocale) {
        countryLocales.push(locale);
      } else {
        matchesCountry
          ? countryLocales.push(locale)
          : notCountryLocales.push(locale);
      }
    }

    setLocalesForSelectedCountry(countryLocales);
    setLocalesNotForSelectedCountry(notCountryLocales);
  }, [selectedCountry, supportedLocales]);

  // Sync shadow field value from the underlying array field
  useEffect(() => {
    if (Array.isArray(field.value)) {
      setShadowFieldValue(field.value[0] as string | undefined);
    }
  }, [field.value]);

  // Prune non-existent values from field when supportedLocales changes.
  useEffect(() => {
    field.onChange(intersection(field.value as string[], supportedLocales));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedLocales]);

  const handleChange = useCallback(
    (value: unknown) => {
      const locale = value as string | undefined;
      setShadowFieldValue(locale);
      field.onChange([locale]);
    },
    [field]
  );

  return (
    <Column>
      <Select
        {...meridianProps}
        error={!!fieldState.error}
        errorMessage={fieldState.error?.message}
        label={label && required ? `${label}*` : label}
        value={shadowFieldValue}
        onBlur={field.onBlur}
        onChange={handleChange}
      >
        <SelectOptionGroup label={t('form_locales_country_locales_group')}>
          {localesForSelectedCountry.map((id) => (
            <SelectOption
              data-testid={getDataTestId(meridianProps, id)}
              key={id}
              label={id}
              value={id}
            />
          ))}
        </SelectOptionGroup>
        <SelectOptionGroup label={t('form_locales_non_country_locales_group')}>
          {localesNotForSelectedCountry.map((id) => (
            <SelectOption
              data-testid={getDataTestId(meridianProps, id)}
              key={id}
              label={id}
              value={id}
            />
          ))}
        </SelectOptionGroup>
      </Select>
    </Column>
  );
};
